import React, { useState } from 'react'
import { Dropdown, Input } from 'semantic-ui-react'
import styled from 'styled-components'
import classNames from 'classnames'

import { Loader } from 'shared/components/Loader'
import EasyToClickIcon from './EasyToClickIcon'
import EasyToClickInlineButton from './EasyToClickInlineButton'

/**
 * @template T
 * @typedef {import('immutable').Set<T>} Set
 */

/**
 * @typedef {import('components/CustomerTagInput').CustomerTag} CustomerTag
 *
 * @param {object} props
 * @param {boolean} props.loading
 * @param {CustomerTag[]} props.allTags
 * @param {Set<CustomerTag>} props.selectedTagSet
 * @param {function} props.onSelectTag
 * @param {() => void} props.onClickCreateNewTag
 * @param {(tag: CustomerTag) => void} props.onClickModifyTag
 */
export default function CustomerTagSelection({
  loading,
  allTags,
  selectedTagSet,
  onSelectTag,
  onClickCreateNewTag,
  onClickModifyTag,
}) {
  const [tagSearch, setTagSearch] = useState('')
  function clearSearch() {
    setTagSearch('')
  }

  return (
    <React.Fragment>
      <div className="relative">
        <InputSearch
          icon="far fa-magnifying-glass"
          iconPosition="left"
          className="search"
          placeholder="Search tags"
          value={tagSearch}
          onChange={(e) => setTagSearch(e.target.value)}
        />
        <i
          onClick={clearSearch}
          className={classNames('link grey icon fas fa-xmark', { invisible: !tagSearch })}
          style={{
            position: 'absolute',
            top: '50%',
            right: 14,
            marginRight: 0,
            transform: 'translateY(-50%)',
          }}
        />
      </div>
      <TagListSection
        loading={loading}
        tagSearch={tagSearch}
        allTags={allTags}
        selectedTagSet={selectedTagSet}
        onClickModifyTag={onClickModifyTag}
        onSelectTag={onSelectTag}
      />
      <Dropdown.Divider className="mb0" />
      <Dropdown.Item className="create-tag" style={{ cursor: 'initial' }}>
        <EasyToClickInlineButton
          className="ui primary basic button center"
          onClick={onClickCreateNewTag}
        >
          New tag
        </EasyToClickInlineButton>
      </Dropdown.Item>
    </React.Fragment>
  )
}

/**
 *
 * @param {object} props
 * @param {boolean} props.loading
 * @param {string} props.tagSearch
 * @param {CustomerTag[]} props.allTags
 * @param {Set<CustomerTag>} props.selectedTagSet
 * @param {(tag: CustomerTag) => void} props.onClickModifyTag
 * @param {function} props.onSelectTag
 */
function TagListSection({
  loading,
  tagSearch,
  allTags,
  selectedTagSet,
  onClickModifyTag,
  onSelectTag,
}) {
  if (loading) {
    return <Loader withContainer containerHeight={100} />
  }

  const hasTags = allTags && allTags.length

  if (hasTags) {
    const displayingTags = allTags.filter((tag) =>
      tag.name.toLowerCase().includes(tagSearch.toLowerCase())
    )
    if (displayingTags.length) {
      return (
        <React.Fragment>
          <Dropdown.Divider />
          <TagList
            loading={false}
            tags={displayingTags}
            selectedTagSet={selectedTagSet}
            onClickModifyTag={onClickModifyTag}
            onSelectTag={onSelectTag}
          />
        </React.Fragment>
      )
    }

    return null
  }

  return null
}

/** @type {typeof Input} */
const InputSearch = styled(Input)`
  && {
    display: flex;
    margin: 0.5rem !important;
  }

  &&& > input {
    padding-right: 28px !important;
  }
`

/**
 *
 * @param {object} props
 * @param {CustomerTag[]} props.tags
 * @param {Set<CustomerTag>} props.selectedTagSet
 * @param {boolean} props.loading
 * @param {function} props.onSelectTag
 * @param {(tag: CustomerTag) => void} props.onClickModifyTag
 */
function TagList({ loading, tags, selectedTagSet, onSelectTag, onClickModifyTag }) {
  return (
    <Dropdown.Menu scrolling>
      {loading ? (
        <Loader withContainer />
      ) : (
        tags.map((tag) => (
          <Tag role="option" className="item" key={tag.name} onClick={() => onSelectTag(tag)}>
            <i
              className={classNames('fas fa-check mr-1', {
                invisible: !selectedTagSet.toArray().some(({ name }) => name === tag.name),
              })}
            />
            <span className="text">{tag.name}</span>
            <span className="ml-auto">
              <EasyToClickIcon
                className="link grey icon far fa-pencil"
                style={{ marginLeft: 8 }}
                onClick={(e) => {
                  e.stopPropagation()
                  onClickModifyTag(tag)
                }}
              />
            </span>
          </Tag>
        ))
      )}
    </Dropdown.Menu>
  )
}

const Tag = styled.div`
  &&&& {
    display: flex;
    align-items: center;
  }

  > span.text {
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/CustomerTagSelection.js